import React, { useEffect, useRef, useState } from 'react';
import { certifications } from '../data/mock';
import { Award, ExternalLink } from 'lucide-react';

const Certifications = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-cycle through certification cards
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveCardIndex((prev) => (prev + 1) % certifications.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-14 md:py-20 bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 transition-colors duration-500 relative overflow-hidden">
      {/* Animated background decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-1/4 w-64 h-64 bg-gradient-to-br from-gray-200/40 to-transparent dark:from-gray-700/30 rounded-full blur-3xl animate-morph" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-tl from-gray-300/30 to-transparent dark:from-gray-600/20 rounded-full blur-3xl animate-morph-reverse" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        <h2
          className={`text-4xl md:text-5xl lg:text-6xl font-extralight text-black dark:text-white mb-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
        >
          Certifications
        </h2>

        {/* Animated gradient underline with flowing shimmer effect */}
        <div className="relative h-1 mt-4 mb-16 overflow-hidden">
          <div
            className={`absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-400 to-gray-200 dark:from-white dark:via-gray-400 dark:to-gray-700 transition-all duration-1000 ${isVisible ? 'w-32 opacity-100' : 'w-0 opacity-0'}`}
            style={{ transitionDelay: '200ms' }}
          />
          {/* Animated shimmer overlay */}
          <div
            className={`absolute inset-0 w-32 overflow-hidden ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ transitionDelay: '700ms' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((cert, index) => (
            <div
              key={cert.name}
              className={`group relative bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
                }`}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              {/* Animated rotating border - shows on hover or when active */}
              <div className={`absolute inset-0 transition-opacity duration-500 ${index === activeCardIndex ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}>
                <div className="absolute inset-[-2px] animate-border-spin">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-400/60 to-transparent dark:via-gray-500/60 rounded-lg" />
                </div>
                <div className="absolute inset-[2px] bg-white dark:bg-gray-800 rounded-lg" />
              </div>

              <div className="flex flex-col h-full relative z-10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-black dark:group-hover:bg-white transition-colors duration-300">
                    <Award size={18} className="text-gray-600 dark:text-gray-400 group-hover:text-white dark:group-hover:text-black transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-base font-light text-black dark:text-white mb-1">
                      {cert.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-light">
                      {cert.issuer}
                    </p>
                  </div>
                </div>
                {cert.url && (
                  <a
                    href={cert.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-auto self-end text-xs font-light text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-full px-3 py-1 hover:text-black dark:hover:text-white hover:border-gray-500 dark:hover:border-gray-400 transition-all duration-300"
                  >
                    Show credential <ExternalLink size={12} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Certifications;
